import { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { username: "", password: "", name: "" };

export default function UsersTab() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/users", form);
            toast.success(`Admin "${form.username}" created`);
            setForm(EMPTY);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Remove admin "${u.username || u.email}"? They will lose access immediately.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("Admin removed");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div data-testid="users-tab" className="space-y-8 max-w-3xl">
            <form onSubmit={submit} data-testid="user-create-form" className="card-arena rounded-2xl p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#FFB800] mb-4">
                    Add Admin User
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <Label htmlFor="user-username" className="text-xs uppercase tracking-[0.2em] text-slate-400">
                            Username
                        </Label>
                        <Input
                            id="user-username"
                            data-testid="user-username-input"
                            required
                            minLength={3}
                            maxLength={20}
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                            className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                            placeholder="e.g. coach_jun"
                        />
                    </div>
                    <div>
                        <Label htmlFor="user-pin" className="text-xs uppercase tracking-[0.2em] text-slate-400">
                            PIN (4 digits)
                        </Label>
                        <Input
                            id="user-pin"
                            data-testid="user-pin-input"
                            required
                            inputMode="numeric"
                            pattern="\d{4}"
                            maxLength={4}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value.replace(/\D/g, "") })}
                            className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                            placeholder="1234"
                        />
                    </div>
                    <div>
                        <Label htmlFor="user-name" className="text-xs uppercase tracking-[0.2em] text-slate-400">
                            Display name (optional)
                        </Label>
                        <Input
                            id="user-name"
                            data-testid="user-name-input"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                            placeholder="Coach Jun"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    data-testid="user-create-button"
                    disabled={saving}
                    className="mt-5 flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors duration-200"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Create admin
                </button>
                <p className="text-xs text-slate-500 mt-3">
                    New users get full admin access and log in at /admin with their username and 4-digit PIN.
                </p>
            </form>

            <div className="card-arena rounded-2xl overflow-hidden">
                <p className="px-6 pt-5 pb-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Admin Users
                </p>
                {users === null ? (
                    <p className="px-6 pb-6 text-slate-500 font-stat text-sm">Loading…</p>
                ) : users.length === 0 ? (
                    <p className="px-6 pb-6 text-slate-500 font-stat text-sm">No users found.</p>
                ) : (
                    <div className="divide-y divide-[#1c1c28]">
                        {users.map((u) => (
                            <div
                                key={u.id}
                                data-testid={`user-row-${u.id}`}
                                className="flex items-center gap-3 px-6 py-3.5"
                            >
                                <div className="w-9 h-9 rounded-xl bg-[#FF5500]/15 border border-[#FF5500]/40 flex items-center justify-center shrink-0">
                                    <ShieldCheck className="w-4 h-4 text-[#FF5500]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-white font-semibold truncate">
                                        {u.name || u.username || u.email}
                                        {u.id === me?.id && (
                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-[#FFB800] border border-[#FFB800]/40 rounded-full px-2 py-0.5">
                                                You
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-slate-500 font-stat truncate">
                                        {u.username ? `${u.username} • PIN login` : u.email}
                                    </p>
                                </div>
                                <button
                                    data-testid={`user-delete-${u.id}`}
                                    onClick={() => remove(u)}
                                    disabled={u.id === me?.id}
                                    className="p-2 rounded-lg text-slate-500 hover:text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-30 disabled:hover:text-slate-500 disabled:hover:bg-transparent transition-colors duration-200"
                                    title={u.id === me?.id ? "You cannot delete your own account" : "Remove admin"}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
